import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Slider } from "@/components/ui/slider";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Loader2, 
  AlertCircle, 
  ClipboardList,
  Star,
  GripVertical,
  CheckCircle2,
  Send,
  PartyPopper,
  Upload,
  File,
  X,
  FileText,
  Image as ImageIcon
} from "lucide-react";
import { Reorder, useDragControls } from "framer-motion";
import { API_URL } from "@/lib/api";
import { 
  QuestionType, 
  DEFAULT_DATE_TIME_CONFIG, 
  HeatMapAnswer, 
  HeatMapClick, 
  HeatMapRegion, 
  isHeatMapAnswer,
  PickGroupRankAnswer,
  isPickGroupRankAnswer,
  TerminationType,
  OptionItem,
} from "@/types/survey";
import { 
  getNextQuestion, 
  shouldShowQuestion,
  randomizeOptionsWithAnchors,
  SurveyFlowState,
} from "@/lib/surveyLogic";
import { VideoResponseRecorder, VideoUploadValue } from "@/components/VideoResponseRecorder";
import { PickGroupRankInput } from "@/components/PickGroupRankInput";
import { useIsMobile } from "@/hooks/use-mobile";
import { useQuotaCheck } from "@/hooks/useQuotaCheck";

// Public survey data interface (subset of full Survey)
interface PublicSurvey {
  id: number;
  title: string;
  description?: string;
  questions: QuestionType[];
  settings: { 
    anonymous?: boolean;
    randomizeQuestions?: boolean;
    anchoredQuestionIds?: string[];
    quotasEnabled?: boolean;  // Required for real-time quota checks
  };
  // Root-level fields (guide compatibility)
  randomizeQuestions?: boolean;
  anchoredQuestionIds?: string[];
}

// ============================================
// Question Randomization (Phase 1.7)
// ============================================

/**
 * Randomize question order while respecting anchored questions.
 * Anchored questions (e.g., intro, consent, demographics) stay in place.
 */
function randomizeQuestionOrder(
  questions: QuestionType[],
  anchoredIds?: string[]
): QuestionType[] {
  if (!questions || questions.length <= 1) return questions;
  
  const anchoredSet = new Set(anchoredIds || []);
  
  // Separate anchored and non-anchored questions
  const regular: QuestionType[] = [];
  const anchored: { question: QuestionType; index: number }[] = [];
  
  questions.forEach((question, index) => {
    if (anchoredSet.has(question.id)) {
      anchored.push({ question, index });
    } else {
      regular.push(question);
    }
  });
  
  // Fisher-Yates shuffle non-anchored questions
  const shuffled = [...regular];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  
  // Re-insert anchored questions at original positions
  const result: QuestionType[] = [...shuffled];
  anchored
    .sort((a, b) => a.index - b.index)
    .forEach(({ question, index }) => {
      const insertAt = Math.min(index, result.length);
      result.splice(insertAt, 0, question);
    });
  
  return result;
}

export default function PublicSurvey() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [imageSignedUrls, setImageSignedUrls] = useState<Record<string, string>>({});
  const fetchedImageUrlsRef = useRef<Set<string>>(new Set());
  // Track share token to reset cache when survey changes (belt-and-suspenders with route remount)
  const lastShareTokenRef = useRef<string | null>(null);
  const isMobile = useIsMobile();
  
  // ============================================
  // Survey Flow State (Phase 4.2)
  // ============================================
  const [flowState, setFlowState] = useState<SurveyFlowState>({
    currentQuestionIndex: 0,
    answers: {},
    visitedQuestions: [],
    terminated: false,
  });
  
  // Track pending termination state (for submitting screen-outs before showing termination screen)
  const [pendingTermination, setPendingTermination] = useState<{
    terminationType: TerminationType;
    terminationMessage?: string;
    terminatedAtQuestionId?: string;
  } | null>(null);

  // Real-time quota checking hook
  const { checkQuota, isChecking } = useQuotaCheck();

  /**
   * Check if a question has segmentation rules that need quota checking.
   * Rules without segment names are considered incomplete/draft and skipped.
   */
  const hasSegmentationRules = useCallback((question: QuestionType): boolean => {
    return !!(
      question.segmentationRules &&
      question.segmentationRules.length > 0 &&
      question.segmentationRules.some(r => r.segmentName?.trim())
    );
  }, []);

  // Fetch survey by share token (public endpoint, no auth)
  const { data: survey, isLoading, error } = useQuery<PublicSurvey>({
    queryKey: ["public-survey", shareToken],
    queryFn: async () => {
      const url = API_URL 
        ? `${API_URL}/api/public/surveys/${shareToken}`
        : `/api/public/surveys/${shareToken}`;
      const res = await fetch(url);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || "Survey not found");
      }
      return res.json();
    },
    retry: false,
    // Prevent re-randomization of options on tab refocus
    refetchOnWindowFocus: false,
  });

  // Submit response mutation
  const submitMutation = useMutation({
    mutationFn: async () => {
      const url = API_URL 
        ? `${API_URL}/api/public/surveys/${shareToken}/responses`
        : `/api/public/surveys/${shareToken}/responses`;
      
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          answers,
          metadata: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenWidth: window.innerWidth,
            screenHeight: window.innerHeight,
            // Timezone info for datetime reconstruction
            timezoneOffsetMinutes: new Date().getTimezoneOffset(),
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
            // Additional device/source info
            referrer: document.referrer || null,
            devicePixelRatio: window.devicePixelRatio || 1,
            fullScreenWidth: screen.width,
            fullScreenHeight: screen.height,
            isTouchDevice: navigator.maxTouchPoints > 0,
            platform: navigator.platform || null,
          },
        }),
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.message || "Failed to submit response");
      }
      
      return data;
    },
    onSuccess: () => {
      // If there was a pending termination, finalize it after successful submission
      if (pendingTermination) {
        setFlowState(prev => ({
          ...prev,
          terminated: true,
          terminationType: pendingTermination.terminationType,
          terminationMessage: pendingTermination.terminationMessage,
          terminatedAtQuestionId: pendingTermination.terminatedAtQuestionId,
        }));
        setPendingTermination(null);
      }
    },
    onError: (error: Error) => {
      setSubmitError(error.message);
      // Even on error, still show termination screen for screen-outs
      // (we don't want to trap user in a loop)
      if (pendingTermination) {
        setFlowState(prev => ({
          ...prev,
          terminated: true,
          terminationType: pendingTermination.terminationType,
          terminationMessage: pendingTermination.terminationMessage,
          terminatedAtQuestionId: pendingTermination.terminatedAtQuestionId,
        }));
        setPendingTermination(null);
      }
    },
  });

  // Randomized question order - computed once per survey load (Phase 1.7)
  const randomizedQuestions = useMemo(() => {
    if (!survey) return [];

    const shouldRandomize =
      survey.settings?.randomizeQuestions ?? survey.randomizeQuestions ?? false;

    const anchoredIds =
      survey.settings?.anchoredQuestionIds ?? survey.anchoredQuestionIds ?? [];

    return shouldRandomize
      ? randomizeQuestionOrder(survey.questions, anchoredIds)
      : survey.questions;
  }, [survey]);

  // Memoize randomized options per question - prevents re-randomization on answer changes
  // Options are randomized once per survey load and stay stable during the session
  const memoizedDisplayOptions = useMemo(() => {
    const optionsMap: Record<string, OptionItem[]> = {};
    
    for (const question of randomizedQuestions) {
      if (question.randomizeOptions && question.options) {
        optionsMap[question.id] = randomizeOptionsWithAnchors(question.options, question.optionLogic);
      } else {
        optionsMap[question.id] = question.options || [];
      }
    }
    
    return optionsMap;
  }, [randomizedQuestions]);

  // Build questionsById map for logic evaluation (required for row_equals/row_not_equals)
  const questionsById = useMemo(() => {
    return new Map(randomizedQuestions.map(q => [q.id, q]));
  }, [randomizedQuestions]);

  // Fetch signed URLs for R2 images in image_choice questions
  useEffect(() => {
    if (!survey?.questions) return;

    // Reset fetch cache if survey changed (belt-and-suspenders with route remount)
    // Note: We only clear the ref, not the state - fresh fetches will populate new data
    if (lastShareTokenRef.current !== shareToken) {
      lastShareTokenRef.current = shareToken;
      fetchedImageUrlsRef.current.clear();
    }

    const fetchSignedUrls = async () => {
      const urlsToFetch: string[] = [];
      
      // Collect all R2 keys from image_choice questions (using ref to check already-fetched)
      for (const question of survey.questions) {
        if (question.type === "image_choice" && question.imageOptions?.images) {
          for (const image of question.imageOptions.images) {
            if (image.url && image.url.startsWith("survey-uploads/") && !fetchedImageUrlsRef.current.has(image.url)) {
              urlsToFetch.push(image.url);
            }
          }
        }
        
        // Collect heat_map image keys
        if (question.type === "heat_map" && question.heatMapConfig?.imageKey) {
          const imageKey = question.heatMapConfig.imageKey;
          if (imageKey.startsWith("survey-uploads/") && !fetchedImageUrlsRef.current.has(imageKey)) {
            urlsToFetch.push(imageKey);
          }
        }
      }

      if (urlsToFetch.length === 0) return;

      // Fetch signed URLs for each R2 key
      const urls: Record<string, string> = {};
      
      for (const key of urlsToFetch) {
        try {
          const url = API_URL 
            ? `${API_URL}/api/public/surveys/${shareToken}/signed-url`
            : `/api/public/surveys/${shareToken}/signed-url`;
          
          const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ key }),
          });

          if (response.ok) {
            const data = await response.json();
            urls[key] = data.url;
            fetchedImageUrlsRef.current.add(key);
          }
        } catch (error) {
          console.error("Failed to fetch signed URL for:", key, error);
        }
      }

      if (Object.keys(urls).length > 0) {
        setImageSignedUrls(prev => ({ ...prev, ...urls }));
      }
    };

    fetchSignedUrls();
  }, [survey?.questions, shareToken]);

  // Helper to get display URL for an image (handles both external URLs and R2 keys)
  const getImageDisplayUrl = (url: string): string | null => {
    if (!url) return null;
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    return imageSignedUrls[url] || null;
  };

  // Handle answer changes - memoized to prevent infinite loops in child components
  const handleAnswer = useCallback((questionId: string, value: AnswerValue) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    // Also update flowState answers for logic evaluation
    setFlowState(prev => ({
      ...prev,
      answers: { ...prev.answers, [questionId]: value }
    }));
    setSubmitError(null);
  }, []);

  // ============================================
  // Navigation with Logic Evaluation (Phase 4.2)
  // ============================================

  // Handle next question navigation with logic evaluation and real-time quota checking
  const handleNext = useCallback(async () => {
    if (!survey || flowState.terminated) return;
    
    const questions = randomizedQuestions;
    const currentQuestion = questions[flowState.currentQuestionIndex];
    const currentAnswer = flowState.answers[currentQuestion.id];
    
    // Store the current answer in updated answers object
    const updatedAnswers = {
      ...flowState.answers,
      [currentQuestion.id]: currentAnswer,
    };
    
    // Check quota BEFORE proceeding if this question has segmentation rules
    if (survey.settings?.quotasEnabled && hasSegmentationRules(currentQuestion)) {
      // Guard: shareToken is required for quota check (route guarantees it exists)
      if (!shareToken) {
        console.error("Missing shareToken for quota check");
        return;
      }
      
      const quotaResult = await checkQuota(
        shareToken,
        currentQuestion.id,
        updatedAnswers,
        true  // cumulative = true: check ALL segmentation rules up to this question
      );
      
      // Handle 4xx errors (survey closed, not found, etc.)
      if (quotaResult.shouldTerminate) {
        setPendingTermination({
          terminationType: "disqualified",
          terminationMessage: quotaResult.error || "This survey is no longer available.",
          terminatedAtQuestionId: currentQuestion.id,
        });
        submitMutation.mutate();
        return;
      }
      
      if (quotaResult.quotaFull) {
        // Update segment in flow state (before submission)
        setFlowState(prev => ({
          ...prev,
          answers: updatedAnswers,
          segment: quotaResult.segment || prev.segment,
        }));
        
        // Use existing termination submission pattern
        setPendingTermination({
          terminationType: "quota_full",
          terminationMessage: undefined,
          terminatedAtQuestionId: currentQuestion.id,
        });
        
        // Submit partial response before showing termination screen
        submitMutation.mutate();
        return;
      }
    }
    
    // Continue with normal flow - evaluate skip logic
    const result = getNextQuestion(
      questions,
      flowState.currentQuestionIndex,
      updatedAnswers,
      currentAnswer as string | string[]
    );
    
    if (result.terminated) {
      // Update segment in flow state
      setFlowState(prev => ({
        ...prev,
        answers: updatedAnswers,
        segment: result.segment || prev.segment,
      }));
      
      // For non-completed terminations (screen_out, quota_full, etc.),
      // submit the partial response before showing the termination screen
      setPendingTermination({
        terminationType: result.terminationType || "screen_out",
        terminationMessage: result.terminationMessage,
        terminatedAtQuestionId: currentQuestion.id,
      });
      
      // Trigger submission - the onSuccess/onError handlers will set the final terminated state
      submitMutation.mutate();
    } else if (result.nextIndex >= 0) {
      // Normal navigation to next question
      setFlowState(prev => ({
        ...prev,
        answers: updatedAnswers,
        currentQuestionIndex: result.nextIndex,
        visitedQuestions: [...prev.visitedQuestions, currentQuestion.id],
        segment: result.segment || prev.segment,
      }));
    } else {
      // Survey complete - submission handled separately
      setFlowState(prev => ({
        ...prev,
        answers: updatedAnswers,
        segment: result.segment || prev.segment,
      }));
    }
  }, [survey, randomizedQuestions, flowState, submitMutation, checkQuota, hasSegmentationRules, shareToken]);

  // Handle back navigation (respects visited question history)
  const handleBack = useCallback(() => {
    setFlowState(prev => {
      if (prev.visitedQuestions.length === 0) return prev;
      
      const newVisited = [...prev.visitedQuestions];
      const previousQuestionId = newVisited.pop();
      
      if (!survey || !previousQuestionId) return prev;
      
      const previousIndex = randomizedQuestions.findIndex(q => q.id === previousQuestionId);
      
      return {
        ...prev,
        currentQuestionIndex: previousIndex >= 0 ? previousIndex : 0,
        visitedQuestions: newVisited,
        terminated: false,
        terminationType: undefined,
        terminationMessage: undefined,
      };
    });
  }, [survey, randomizedQuestions]);

  // Check if a question type is a matrix type (defined early for use in isCurrentQuestionAnswered)
  const isMatrixTypeCheck = (type: string): boolean => {
    return ["radio_grid", "checkbox_grid", "dropdown_matrix", "text_matrix", "numeric_matrix", "ranking_matrix"].includes(type);
  };

  // Validate matrix answers - all rows/cells must be answered (defined early for use in isCurrentQuestionAnswered)
  const isMatrixCompleteCheck = (
    answer: Record<string, unknown> | undefined | null,
    question: QuestionType
  ): boolean => {
    if (!answer || typeof answer !== "object") return false;
    if (!question.rows || question.rows.length === 0) return true;
    
    const numRows = question.rows.length;
    const numCols = question.columns?.length || 0;
    
    switch (question.type) {
      case "radio_grid":
        // Each row (0 to numRows-1) must have an entry
        for (let i = 0; i < numRows; i++) {
          if ((answer as Record<string, unknown>)[i] === undefined && 
              (answer as Record<string, unknown>)[String(i)] === undefined) {
            return false;
          }
        }
        return true;
        
      case "checkbox_grid":
        // Each row must have at least one selection (non-empty array)
        for (let i = 0; i < numRows; i++) {
          const rowValue = (answer as Record<string, unknown>)[i] ?? (answer as Record<string, unknown>)[String(i)];
          if (!Array.isArray(rowValue) || rowValue.length === 0) {
            return false;
          }
        }
        return true;
        
      case "dropdown_matrix":
      case "text_matrix":
      case "numeric_matrix":
      case "ranking_matrix":
        // All cells (rowIndex-colIndex) must have non-empty values
        for (let row = 0; row < numRows; row++) {
          for (let col = 0; col < numCols; col++) {
            const cellKey = `${row}-${col}`;
            const cellValue = (answer as Record<string, unknown>)[cellKey];
            if (cellValue === undefined || cellValue === null || cellValue === "") {
              return false;
            }
          }
        }
        return true;
        
      default:
        return true;
    }
  };

  // Check if current question is answered (for enabling Next button)
  const isCurrentQuestionAnswered = useMemo(() => {
    if (!survey || randomizedQuestions.length === 0) return false;
    const currentQuestion = randomizedQuestions[flowState.currentQuestionIndex];
    if (!currentQuestion) return false;
    
    const answer = flowState.answers[currentQuestion.id];
    
    // Check if question is answered based on type
    if (answer === undefined || answer === null || answer === "") return false;
    if (Array.isArray(answer) && answer.length === 0) return false;
    
    // Special validation for matrix questions - ALL cells must be filled
    if (isMatrixTypeCheck(currentQuestion.type)) {
      return isMatrixCompleteCheck(answer as Record<string, unknown>, currentQuestion);
    }
    
    return true;
  }, [survey, randomizedQuestions, flowState.currentQuestionIndex, flowState.answers]);

  // Check if we're at the last visible question
  const isLastQuestion = useMemo(() => {
    if (!survey || randomizedQuestions.length === 0) return false;
    
    // Check if there are any more visible questions after current
    for (let i = flowState.currentQuestionIndex + 1; i < randomizedQuestions.length; i++) {
      if (shouldShowQuestion(randomizedQuestions[i], flowState.answers, questionsById)) {
        return false;
      }
    }
    return true;
  }, [survey, randomizedQuestions, flowState.currentQuestionIndex, flowState.answers, questionsById]);

  // Validate all required questions before submission
  const validateAnswers = (): string[] => {
    if (!survey) return [];
    
    const missingRequired: string[] = [];
    
    // Build set of questions that were actually shown to the user
    // This includes visited questions + the current question
    const currentId = randomizedQuestions[flowState.currentQuestionIndex]?.id;
    const shownIds = new Set<string>([
      ...flowState.visitedQuestions,
      ...(currentId ? [currentId] : []),
    ]);
    
    for (const question of survey.questions) {
      // Skip questions not shown due to display conditions
      if (!shouldShowQuestion(question, flowState.answers, questionsById)) {
        continue;
      }
      
      // Skip questions not visited due to skip logic jumps
      if (!shownIds.has(question.id)) {
        continue;
      }
      
      if (question.required) {
        const answer = answers[question.id];
        
        // Special validation for matrix questions
        if (isMatrixTypeCheck(question.type)) {
          if (!isMatrixCompleteCheck(answer as Record<string, unknown>, question)) {
            missingRequired.push(question.question);
          }
          continue;
        }
        
        // Special validation for pick_group_rank questions
        if (question.type === "pick_group_rank" && question.pickGroupRankConfig) {
          const config = question.pickGroupRankConfig;
          const pgrAnswer = isPickGroupRankAnswer(answer) ? answer : null;
          
          if (!pgrAnswer) {
            missingRequired.push(question.question);
            continue;
          }
          
          // Check if all items must be sorted
          if (config.requireAllItemsSorted && pgrAnswer.unsorted.length > 0) {
            missingRequired.push(question.question);
            continue;
          }
          
          // Check minimum total sorted
          const totalSorted = Object.values(pgrAnswer.groups).reduce((sum, items) => sum + items.length, 0);
          if (config.validation?.minTotalSorted !== undefined && totalSorted < config.validation.minTotalSorted) {
            missingRequired.push(question.question);
            continue;
          }
          
          continue;
        }
        
        // Standard validation for other question types
        const isEmpty = 
          answer === undefined || 
          answer === null || 
          answer === "" ||
          (Array.isArray(answer) && answer.length === 0) ||
          (typeof answer === "object" && !Array.isArray(answer) && Object.keys(answer as object).length === 0);
        
        if (isEmpty) {
          missingRequired.push(question.question);
        }
      }
    }
    
    return missingRequired;
  };

  // Handle form submission
  const handleSubmit = () => {
    setSubmitError(null);
    
    // Validate required questions (including proper matrix validation)
    const missingRequired = validateAnswers();
    if (missingRequired.length > 0) {
      setSubmitError(`Please complete all required questions. Missing: ${missingRequired.join(", ")}`);
      return;
    }
    
    submitMutation.mutate();
  };

  // Calculate progress
  // Calculate progress based on current question position (Phase 4.2)
  const progress = survey && randomizedQuestions.length > 0
    ? ((flowState.currentQuestionIndex + 1) / randomizedQuestions.length) * 100 
    : 0;

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-200">Loading survey...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !survey) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/95 dark:bg-card/95 backdrop-blur">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-500 dark:text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-foreground mb-2">Survey Not Available</h2>
            <p className="text-muted-foreground">
              {error instanceof Error ? error.message : "This survey may have been closed or doesn't exist."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Termination submission in progress - show loading state
  if (pendingTermination && submitMutation.isPending) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-purple-400 mx-auto mb-4" />
          <p className="text-purple-200">Recording your response...</p>
        </div>
      </div>
    );
  }

  // Termination state - Show termination screen (Phase 4.2)
  if (flowState.terminated && flowState.terminationType) {
    return (
      <TerminationScreen
        type={flowState.terminationType}
        message={flowState.terminationMessage}
        surveyTitle={survey?.title || "Survey"}
      />
    );
  }

  // Success state - Thank you screen
  if (submitMutation.isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-white/95 dark:bg-card/95 backdrop-blur shadow-2xl">
          <CardContent className="pt-10 pb-10 text-center">
            <div className="relative mb-6">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto">
                <CheckCircle2 className="h-10 w-10 text-green-600 dark:text-green-400" />
              </div>
              <div className="absolute -top-2 -right-2 animate-bounce">
                <PartyPopper className="h-8 w-8 text-purple-500 dark:text-purple-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-foreground mb-3">Thank You!</h2>
            <p className="text-muted-foreground mb-6">
              Your response has been submitted successfully. We appreciate your time and feedback!
            </p>
            <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-purple-700 dark:text-purple-300">
                <span className="font-semibold">{survey.title}</span>
              </p>
              <p className="text-xs text-purple-500 dark:text-purple-400 mt-1">
                Response recorded
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              You can close this window now
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 py-6 px-4 sm:py-10">
      <div className="max-w-2xl mx-auto">
        {/* Survey Header */}
        <Card className="mb-6 bg-white/95 dark:bg-card/95 backdrop-blur shadow-xl border-0">
          <CardHeader className="pb-4">
            <div className="flex items-start gap-3 mb-2">
              <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="h-5 w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-foreground leading-tight">
                  {survey.title}
                </h1>
                {survey.description && (
                  <p className="text-muted-foreground mt-2 text-sm sm:text-base">
                    {survey.description}
                  </p>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="flex items-center gap-3">
              <Progress value={progress} className="flex-1 h-2" />
              <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">
                {Math.round(progress)}%
              </span>
            </div>
            <p className="text-xs text-muted-foreground/70 mt-2">
              Question {flowState.currentQuestionIndex + 1} of {randomizedQuestions.length}
            </p>
          </CardContent>
        </Card>

        {/* Current Question - Step-by-step flow (Phase 4.2) */}
        {randomizedQuestions.length > 0 && flowState.currentQuestionIndex < randomizedQuestions.length && (() => {
          const question = randomizedQuestions[flowState.currentQuestionIndex];
          
          // Skip questions that shouldn't be shown based on display conditions
          if (!shouldShowQuestion(question, flowState.answers, questionsById)) {
            // Auto-advance to next visible question
            handleNext();
            return null;
          }
          
          // Get memoized options (randomized once per survey load, stable across re-renders)
          const displayOptions = memoizedDisplayOptions[question.id] || question.options;
          
          // Create a modified question with the memoized options
          const displayQuestion = displayOptions !== question.options
            ? { ...question, options: displayOptions }
            : question;
          
          return (
            <Card 
              key={question.id} 
              className="bg-white/95 dark:bg-card/95 backdrop-blur shadow-lg border-0 overflow-hidden"
            >
              <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 border-b border-purple-100 dark:border-purple-800 py-4">
                <div className="flex items-start gap-3">
                  <span className="bg-purple-600 text-white rounded-full w-7 h-7 sm:w-8 sm:h-8 flex items-center justify-center text-sm font-semibold flex-shrink-0">
                    {flowState.currentQuestionIndex + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-foreground text-sm sm:text-base leading-snug">
                      {question.question}
                    </h3>
                    {question.required && (
                      <span className="text-xs text-[#e88463] mt-1 inline-block">* Required</span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 sm:p-6">
                <QuestionInput 
                  question={displayQuestion} 
                  value={answers[question.id]} 
                  onChange={(value) => handleAnswer(question.id, value)}
                  shareToken={shareToken || ""}
                  isMobile={isMobile}
                  getImageDisplayUrl={getImageDisplayUrl}
                />
              </CardContent>
            </Card>
          );
        })()}

        {/* Navigation Section (Phase 4.2) */}
        <div className="mt-8 mb-8 space-y-4">
          {/* Error Alert */}
          {submitError && (
            <Alert variant="destructive" className="bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-800 dark:text-red-300">
                {submitError}
              </AlertDescription>
            </Alert>
          )}
          
          {/* Navigation Buttons */}
          <div className="flex gap-3">
            {/* Back Button */}
            <Button 
              variant="outline"
              className="flex-1 py-6 text-lg font-semibold"
              onClick={handleBack}
              disabled={flowState.visitedQuestions.length === 0}
            >
              Back
            </Button>
            
            {/* Next or Submit Button */}
            {isLastQuestion ? (
              <Button 
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                onClick={handleSubmit}
                disabled={submitMutation.isPending || (randomizedQuestions[flowState.currentQuestionIndex]?.required && !isCurrentQuestionAnswered)}
              >
                {submitMutation.isPending ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5 mr-2" />
                    Submit
                  </>
                )}
              </Button>
            ) : (
              <Button 
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground py-6 text-lg font-semibold shadow-lg transition-all hover:shadow-xl disabled:opacity-50"
                onClick={handleNext}
                disabled={isChecking || (randomizedQuestions[flowState.currentQuestionIndex]?.required && !isCurrentQuestionAnswered)}
              >
                {isChecking ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Checking...
                  </>
                ) : (
                  "Next"
                )}
              </Button>
            )}
          </div>
          
          <p className="text-center text-purple-300 text-xs">
            {survey.settings?.anonymous 
              ? "Your response is anonymous" 
              : "Your response will be recorded"}
          </p>
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-purple-300 text-xs">
            Powered by <span className="font-semibold">SurvAI</span>
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Heat Map Utility Functions
// ============================================

/**
 * Clamp a value between min and max (inclusive)
 */
const clamp = (value: number, min: number, max: number): number => {
  return Math.max(min, Math.min(max, value));
};

/**
 * Point-in-polygon test using ray casting algorithm.
 * Handles convex and concave polygons.
 */
const pointInPolygon = (
  xPct: number, 
  yPct: number, 
  pointsPct: Array<{ xPct: number; yPct: number }>
): boolean => {
  if (pointsPct.length < 3) return false;
  
  let inside = false;
  for (let i = 0, j = pointsPct.length - 1; i < pointsPct.length; j = i++) {
    const xi = pointsPct[i].xPct, yi = pointsPct[i].yPct;
    const xj = pointsPct[j].xPct, yj = pointsPct[j].yPct;
    
    if (((yi > yPct) !== (yj > yPct)) && (xPct < (xj - xi) * (yPct - yi) / (yj - yi) + xi)) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Determines which region (if any) a click falls within.
 * Returns region ID, "other" if regions exist but click is outside, or undefined if no regions.
 */
const getRegionForClick = (
  xPct: number, 
  yPct: number, 
  regions: HeatMapRegion[] | undefined
): string | "other" | undefined => {
  if (!regions || regions.length === 0) return undefined;
  
  for (const region of regions) {
    if (region.shape === "rect") {
      // Rectangle: check if point is within bounds
      if (
        xPct >= region.xPct && 
        xPct <= region.xPct + region.wPct &&
        yPct >= region.yPct && 
        yPct <= region.yPct + region.hPct
      ) {
        return region.id;
      }
    } else if (region.shape === "poly") {
      if (pointInPolygon(xPct, yPct, region.pointsPct)) {
        return region.id;
      }
    }
  }
  
  return "other";
};

/**
 * Compact clicks to slots 1..n with no gaps, sorted by timestamp.
 * This ensures deterministic export ordering.
 */
const compactSlots = (clicks: HeatMapClick[]): HeatMapClick[] => {
  return clicks
    .slice()
    .sort((a, b) => a.ts - b.ts)
    .map((click, index) => ({
      ...click,
      slot: index + 1,
    }));
};

// ============================================
// Question Input Component
// ============================================

type AnswerValue = string | number | string[] | Record<string, unknown> | VideoUploadValue | HeatMapAnswer | PickGroupRankAnswer | null | undefined;

interface QuestionInputProps {
  question: QuestionType;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  shareToken: string;
  isMobile: boolean;
  getImageDisplayUrl: (url: string) => string | null;
}

// Separate component for Heat Map to properly use hooks
interface HeatMapQuestionInputProps {
  question: QuestionType;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  getImageDisplayUrl: (url: string) => string | null;
}

function HeatMapQuestionInput({ question, value, onChange, getImageDisplayUrl }: HeatMapQuestionInputProps) {
  const config = question.heatMapConfig;
  const imageKey = config?.imageKey || "";
  const imageUrl = getImageDisplayUrl(imageKey);
  const maxClicks = config?.maxClicks ?? 1;
  const replaceOldest = config?.replaceOldest ?? true;
  const allowDeselect = config?.allowDeselect ?? true;
  const regions = config?.regions;
  const regionsVisibleToRespondent = config?.regionsVisibleToRespondent ?? false;
  
  // Get current answer or initialize empty
  const currentAnswer: HeatMapAnswer = isHeatMapAnswer(value) 
    ? value 
    : { clicks: [], imageNaturalWidth: 0, imageNaturalHeight: 0 };
  
  // Track natural dimensions from image load (properly using useState at top level)
  const [naturalDims, setNaturalDims] = useState<{ width: number; height: number } | null>(
    config?.imageNaturalWidth && config?.imageNaturalHeight 
      ? { width: config.imageNaturalWidth, height: config.imageNaturalHeight }
      : null
  );
  
  const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    if (img.naturalWidth > 0 && img.naturalHeight > 0) {
      setNaturalDims({ width: img.naturalWidth, height: img.naturalHeight });
    }
  };
  
  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    // Must have natural dimensions before accepting clicks
    if (!naturalDims) return;

    const container = e.currentTarget;
    const rect = container.getBoundingClientRect();

    // Calculate percentage coordinates
    const rawXPct = ((e.clientX - rect.left) / rect.width) * 100;
    const rawYPct = ((e.clientY - rect.top) / rect.height) * 100;

    // Epsilon check - ignore clicks significantly outside bounds
    const epsilon = 2; // 2% tolerance
    if (
      rawXPct < -epsilon ||
      rawXPct > 100 + epsilon ||
      rawYPct < -epsilon ||
      rawYPct > 100 + epsilon
    ) {
      return; // Don't preventDefault - allow normal scroll behavior
    }

    // Clamp to valid range
    const xPct = clamp(rawXPct, 0, 100);
    const yPct = clamp(rawYPct, 0, 100);

    // Calculate pixel coordinates (0-indexed)
    const xPx = Math.round(
      clamp((xPct / 100) * naturalDims.width, 0, naturalDims.width - 1)
    );
    const yPx = Math.round(
      clamp((yPct / 100) * naturalDims.height, 0, naturalDims.height - 1)
    );

    // Check for deselect - did they click on an existing dot?
    if (allowDeselect && currentAnswer.clicks.length > 0) {
      const dotRadius = 4; // % for hit testing
      const clickedDot = currentAnswer.clicks.find((click) => {
        const dx = Math.abs(click.xPct - xPct);
        const dy = Math.abs(click.yPct - yPct);
        return dx < dotRadius && dy < dotRadius;
      });

      if (clickedDot) {
        // NOW we're recording a change - capture and prevent default
        e.currentTarget.setPointerCapture(e.pointerId);
        e.preventDefault();

        const remainingClicks = currentAnswer.clicks.filter(
          (c) => c.slot !== clickedDot.slot
        );
        const compactedClicks = compactSlots(remainingClicks);

        onChange({
          ...currentAnswer,
          clicks: compactedClicks,
          imageNaturalWidth: naturalDims.width,
          imageNaturalHeight: naturalDims.height,
        });
        return;
      }
    }

    // Check if we can add a new click
    if (currentAnswer.clicks.length >= maxClicks && !replaceOldest) {
      // At capacity and no replace - ignore this click, don't preventDefault
      return;
    }

    // NOW we're recording a change - capture and prevent default
    e.currentTarget.setPointerCapture(e.pointerId);
    e.preventDefault();

    const regionId = getRegionForClick(xPct, yPct, regions);

    const newClick: HeatMapClick = {
      slot: 0, // assigned during compaction
      xPct: Math.round(xPct * 100) / 100,
      yPct: Math.round(yPct * 100) / 100,
      xPx,
      yPx,
      regionId,
      ts: Date.now(),
    };

    let newClicks: HeatMapClick[];

    if (currentAnswer.clicks.length < maxClicks) {
      newClicks = [...currentAnswer.clicks, newClick];
    } else {
      const sortedByTs = [...currentAnswer.clicks].sort((a, b) => a.ts - b.ts);
      const oldest = sortedByTs[0];
      newClicks = currentAnswer.clicks.filter((c) => c.slot !== oldest.slot);
      newClicks.push(newClick);
    }

    const compactedClicks = compactSlots(newClicks);

    onChange({
      clicks: compactedClicks,
      imageNaturalWidth: naturalDims.width,
      imageNaturalHeight: naturalDims.height,
    });
  };
  
  const isLoading = imageKey && imageKey.startsWith("survey-uploads/") && !imageUrl;
  
  return (
    <div className="space-y-3">
      {/* Heat Map Image Container */}
      <div 
        className="relative inline-block cursor-crosshair select-none"
        style={{ touchAction: "manipulation" }}
        onPointerDown={handlePointerDown}
      >
        {isLoading ? (
          <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
          </div>
        ) : imageUrl ? (
          <>
            <img 
              src={imageUrl}
              alt="Click to indicate areas of interest"
              className="block max-w-full h-auto rounded-lg pointer-events-none"
              onLoad={handleImageLoad}
              draggable={false}
            />
            
            {/* Regions overlay (visible to respondent when configured) */}
            {regionsVisibleToRespondent && regions && regions.length > 0 && regions.map((region) => {
              if (region.shape === "rect") {
                // Visible outline only - no labels (labels obstruct the image)
                return (
                  <div
                    key={region.id}
                    className="absolute border-2 border-gray-500/70 bg-gray-400/15 pointer-events-none rounded-sm"
                    style={{
                      left: `${region.xPct}%`,
                      top: `${region.yPct}%`,
                      width: `${region.wPct}%`,
                      height: `${region.hPct}%`,
                    }}
                  />
                );
              } else if (region.shape === "poly" && region.pointsPct.length >= 3) {
                // Visible outline only - no labels (labels obstruct the image)
                return (
                  <svg
                    key={region.id}
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    viewBox="0 0 100 100"
                    preserveAspectRatio="none"
                  >
                    <polygon
                      points={region.pointsPct.map(p => `${p.xPct},${p.yPct}`).join(' ')}
                      fill="rgba(156, 163, 175, 0.15)"
                      stroke="rgba(107, 114, 128, 0.7)"
                      strokeWidth="1.5"
                    />
                  </svg>
                );
              }
              return null;
            })}
            
            {/* Click dots overlay */}
            {currentAnswer.clicks.map((click) => (
              <div
                key={click.slot}
                className={`
                  absolute w-4 h-4 rounded-full border-2 border-white shadow-lg
                  transform -translate-x-1/2 -translate-y-1/2 pointer-events-none
                  ${allowDeselect ? 'hover:scale-125 transition-transform' : ''}
                `}
                style={{
                  left: `${click.xPct}%`,
                  top: `${click.yPct}%`,
                  backgroundColor: '#e88463', // SurviAI brand color
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
                title={`Click ${click.slot}${click.regionId ? ` (${click.regionId})` : ''}`}
              >
                {/* Slot number inside dot */}
                {maxClicks > 1 && (
                  <span className="absolute inset-0 flex items-center justify-center text-[8px] font-bold text-white">
                    {click.slot}
                  </span>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className="w-full h-48 bg-muted rounded-lg flex items-center justify-center">
            <div className="text-muted-foreground text-center">
              <ImageIcon className="w-12 h-12 mx-auto mb-2" />
              <p className="text-sm">No image available</p>
            </div>
          </div>
        )}
      </div>
      
      {/* Instructions */}
      <p className="text-xs text-muted-foreground text-center">
        {maxClicks === 1 
          ? "Click on the image to indicate your selection"
          : `Click up to ${maxClicks} times on the image`
        }
        {currentAnswer.clicks.length > 0 && (
          <span className="ml-2">
            ({currentAnswer.clicks.length} of {maxClicks} click{maxClicks > 1 ? 's' : ''})
          </span>
        )}
        {allowDeselect && currentAnswer.clicks.length > 0 && (
          <span className="block mt-1 text-muted-foreground/70">Click on a dot to remove it</span>
        )}
      </p>
    </div>
  );
}

// File upload answer type (stored in answers)
interface FileUploadValue {
  key: string;        // R2 object key (private)
  filename: string;   // Original filename
  size: number;       // File size in bytes
  mimetype: string;   // MIME type
}

// ============================================
// Ranking Input Component with Drag & Drop
// ============================================

interface RankingInputProps {
  question: QuestionType;
  value: string[] | undefined;
  onChange: (value: string[]) => void;
}

function RankingInput({ question, value, onChange }: RankingInputProps) {
  const options = question.options || [];
  
  // Initialize items with current value (IDs) or default order
  // Note: Component uses key in parent to remount when options change
  const [itemIds, setItemIds] = useState<string[]>(() => {
    if (value && Array.isArray(value) && value.length === options.length) {
      return value;
    }
    // Default: option IDs in original order
    return options.map(o => o.id);
  });

  // Store onChange in a ref to avoid dependency issues and infinite loops
  const onChangeRef = useRef(onChange);
  
  // Update ref in an effect rather than during render
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Track previous items to avoid unnecessary onChange calls
  const prevItemsRef = useRef<string[]>(itemIds);

  // Update parent when items change (only if actually different)
  useEffect(() => {
    if (itemIds.length > 0 && JSON.stringify(itemIds) !== JSON.stringify(prevItemsRef.current)) {
      prevItemsRef.current = itemIds;
      onChangeRef.current(itemIds);
    }
  }, [itemIds]); // onChange removed from deps - using ref instead

  const handleReorder = (newOrder: string[]) => {
    setItemIds(newOrder);
  };

  // Helper to get option text by ID
  const getOptionText = (id: string): string => {
    return options.find(o => o.id === id)?.text || id;
  };

  if (options.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No options available for ranking.</p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-sm text-muted-foreground mb-3">
        Drag items to rank them (1 = highest preference):
      </p>
      <Reorder.Group 
        axis="y" 
        values={itemIds} 
        onReorder={handleReorder}
        className="space-y-2"
      >
        {itemIds.map((itemId, index) => (
          <RankingItem key={itemId} itemId={itemId} itemText={getOptionText(itemId)} index={index} />
        ))}
      </Reorder.Group>
    </div>
  );
}

// Individual draggable ranking item
interface RankingItemProps {
  itemId: string;
  itemText: string;
  index: number;
}

function RankingItem({ itemId, itemText, index }: RankingItemProps) {
  const dragControls = useDragControls();

  return (
    <Reorder.Item
      value={itemId}
      dragListener={false}
      dragControls={dragControls}
      className="flex items-center gap-3 p-3 bg-muted border border-border rounded-lg cursor-grab active:cursor-grabbing select-none"
      whileDrag={{
        scale: 1.02,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
        backgroundColor: "rgb(243, 232, 255)", // purple-100
        borderColor: "rgb(192, 132, 252)", // purple-400
      }}
      transition={{ duration: 0.2 }}
    >
      <div 
        onPointerDown={(e) => dragControls.start(e)}
        className="touch-none cursor-grab active:cursor-grabbing p-1 -m-1 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded transition-colors"
      >
        <GripVertical className="h-5 w-5 text-muted-foreground" />
      </div>
      <span className="w-7 h-7 bg-purple-600 text-white rounded-full text-sm font-semibold flex items-center justify-center flex-shrink-0">
        {index + 1}
      </span>
      <span className="text-foreground flex-1">{itemText}</span>
    </Reorder.Item>
  );
}

// ============================================
// File Upload Input Component
// Uploads to private R2 bucket, stores key in answer
// ============================================

interface FileUploadInputProps {
  question: QuestionType;
  value: AnswerValue;
  onChange: (value: AnswerValue) => void;
  shareToken: string;
}

function FileUploadInput({ question, value, onChange, shareToken }: FileUploadInputProps) {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const maxSize = question.validation?.maxFileSize || 10; // Default 10MB
  const allowedTypes = question.validation?.fileTypes || ["PDF", "DOC", "DOCX", "images"];
  
  // Cast value to FileUploadValue if it has the right shape
  const fileValue = value && typeof value === 'object' && 'key' in value 
    ? value as unknown as FileUploadValue 
    : null;

  const handleFileSelect = async (file: File) => {
    setError(null);
    
    // Validate file size
    if (file.size > maxSize * 1024 * 1024) {
      setError(`File too large. Maximum size is ${maxSize}MB`);
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("file", file);

      // Simulate progress for better UX
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const url = API_URL 
        ? `${API_URL}/api/public/surveys/${shareToken}/upload`
        : `/api/public/surveys/${shareToken}/upload`;

      const response = await fetch(url, {
        method: "POST",
        body: formData,
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Upload failed");
      }

      // Store the file metadata (with key, not URL) as the answer
      onChange(data.file);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed");
      onChange(null);
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleRemove = () => {
    onChange(null);
    setError(null);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimetype: string) => {
    if (mimetype.startsWith("image/")) {
      return <ImageIcon className="h-8 w-8 text-purple-500 dark:text-purple-400" />;
    }
    if (mimetype.includes("pdf")) {
      return <FileText className="h-8 w-8 text-red-500 dark:text-red-400" />;
    }
    return <File className="h-8 w-8 text-blue-500 dark:text-blue-400" />;
  };

  // Show uploaded file confirmation
  if (fileValue && fileValue.key) {
    return (
      <div className="border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/30 rounded-lg p-4">
        <div className="flex items-center gap-3">
          {getFileIcon(fileValue.mimetype)}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-foreground truncate">{fileValue.filename}</p>
            <p className="text-sm text-muted-foreground">{formatFileSize(fileValue.size)}</p>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
            <button
              type="button"
              onClick={handleRemove}
              className="p-1 text-muted-foreground hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded transition-colors"
              title="Remove file"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        <p className="text-xs text-green-600 dark:text-green-400 mt-2">
          ✓ File uploaded securely
        </p>
      </div>
    );
  }

  // Show upload interface
  return (
    <div className="space-y-2">
      <label
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          relative flex flex-col items-center justify-center
          border-2 border-dashed rounded-lg p-6 text-center
          cursor-pointer transition-all duration-200
          ${isDragOver 
            ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20" 
            : "border-border hover:border-purple-400 dark:hover:border-purple-500 hover:bg-purple-50/50 dark:hover:bg-purple-900/10"
          }
          ${isUploading ? "pointer-events-none opacity-60" : ""}
        `}
      >
        <input
          type="file"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          onChange={handleInputChange}
          disabled={isUploading}
          accept={allowedTypes.map(t => {
            const lower = t.toLowerCase();
            if (lower === "images" || lower === "image") return "image/*";
            if (lower === "pdf") return ".pdf,application/pdf";
            if (lower === "doc") return ".doc,application/msword";
            if (lower === "docx") return ".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
            return `.${lower}`;
          }).join(",")}
        />
        
        {isUploading ? (
          <>
            <Loader2 className="h-10 w-10 text-purple-500 dark:text-purple-400 animate-spin mb-3" />
            <p className="text-sm font-medium text-purple-600 dark:text-purple-400 mb-2">Uploading securely...</p>
            <div className="w-full max-w-xs bg-muted rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">{uploadProgress}%</p>
          </>
        ) : (
          <>
            <Upload className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm font-medium text-foreground mb-1">
              {isDragOver ? "Drop file here" : "Click to upload or drag and drop"}
            </p>
            <p className="text-xs text-muted-foreground">
              {allowedTypes.join(", ")} (max {maxSize}MB)
            </p>
          </>
        )}
      </label>

      {error && (
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm bg-red-50 dark:bg-red-900/30 p-2 rounded">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

// ============================================
// Termination Screen Component (Phase 4.3)
// ============================================

interface TerminationScreenProps {
  type: TerminationType;
  message?: string;
  surveyTitle: string;
}

function TerminationScreen({ type, message, surveyTitle }: TerminationScreenProps) {
  const config: Record<TerminationType, { 
    icon: React.ReactNode; 
    title: string; 
    defaultMessage: string; 
    bgColor: string 
  }> = {
    screen_out: {
      icon: <AlertCircle className="h-10 w-10 text-amber-600 dark:text-amber-400" />,
      title: "Thank You for Your Time",
      defaultMessage: "Based on your responses, you don't qualify for this study. We appreciate your interest!",
      bgColor: "bg-amber-50 dark:bg-amber-900/30",
    },
    thank_and_close: {
      icon: <CheckCircle2 className="h-10 w-10 text-blue-600 dark:text-blue-400" />,
      title: "Thank You",
      defaultMessage: "Thank you for your participation. Your responses have been noted.",
      bgColor: "bg-blue-50 dark:bg-blue-900/30",
    },
    quota_full: {
      icon: <AlertCircle className="h-10 w-10 text-orange-600 dark:text-orange-400" />,
      title: "Thank You for Your Interest",
      defaultMessage: "We have reached our target number of responses for your demographic group. We appreciate your time and interest in participating.",
      bgColor: "bg-orange-50 dark:bg-orange-900/30",
    },
    completed: {
      icon: <PartyPopper className="h-10 w-10 text-green-600 dark:text-green-400" />,
      title: "Thank You!",
      defaultMessage: "Your response has been submitted successfully. We appreciate your time and feedback!",
      bgColor: "bg-green-50 dark:bg-green-900/30",
    },
    disqualified: {
      icon: <AlertCircle className="h-10 w-10 text-red-600 dark:text-red-400" />,
      title: "Survey Ended",
      defaultMessage: "Unfortunately, you don't meet the criteria for this survey.",
      bgColor: "bg-red-50 dark:bg-red-900/30",
    },
  };
  
  const { icon, title, defaultMessage, bgColor } = config[type] || config.screen_out;
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-white/95 dark:bg-card/95 backdrop-blur shadow-2xl">
        <CardContent className="pt-10 pb-10 text-center">
          <div className={`w-20 h-20 ${bgColor} rounded-full flex items-center justify-center mx-auto mb-6`}>
            {icon}
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-3">{title}</h2>
          <p className="text-muted-foreground mb-6">
            {message || defaultMessage}
          </p>
          <div className="bg-purple-50 dark:bg-purple-900/30 rounded-lg p-4">
            <p className="text-sm text-purple-700 dark:text-purple-300 font-semibold">{surveyTitle}</p>
          </div>
          <p className="text-xs text-muted-foreground mt-6">
            You can close this window now
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function QuestionInput({ question, value, onChange, shareToken, isMobile, getImageDisplayUrl }: QuestionInputProps) {
  switch (question.type) {
    case "single_choice": {
      const currentValue = (value as string) || "";
      const isOtherSelected = currentValue.startsWith("__other__:");
      const otherText = isOtherSelected ? currentValue.replace("__other__:", "") : "";
      
      return (
        <RadioGroup 
          value={isOtherSelected ? "__other__" : currentValue} 
          onValueChange={(newValue) => {
            if (newValue === "__other__") {
              onChange("__other__:");
            } else {
              onChange(newValue);
            }
          }}
          className="space-y-2"
        >
          {(question.options || []).map((option) => (
            <label 
              key={option.id} 
              className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-700 cursor-pointer transition-all"
            >
              <RadioGroupItem value={option.id} className="text-purple-600 dark:text-purple-400" />
              <span className="text-foreground text-sm sm:text-base">{option.text}</span>
            </label>
          ))}
          {question.allowOther && (
            <div className="space-y-2">
              <label 
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  isOtherSelected 
                    ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20' 
                    : 'border-border hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-700'
                }`}
              >
                <RadioGroupItem value="__other__" className="text-purple-600 dark:text-purple-400" />
                <span className="text-foreground text-sm sm:text-base">{question.otherLabel || "Other (please specify)"}</span>
              </label>
              {isOtherSelected && (
                <Input
                  value={otherText}
                  onChange={(e) => onChange(`__other__:${e.target.value}`)}
                  placeholder="Please specify..."
                  className="ml-7 w-[calc(100%-1.75rem)] focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
              )}
            </div>
          )}
        </RadioGroup>
      );
    }

    case "multiple_choice": {
      const selectedMultiple = (value as string[]) || [];
      const otherEntry = selectedMultiple.find(item => item.startsWith("__other__:"));
      const isOtherSelected = !!otherEntry;
      const otherText = otherEntry ? otherEntry.replace("__other__:", "") : "";
      
      // Helper to check if an option is exclusive
      const isOptionExclusive = (optionId: string): boolean => {
        const logic = question.optionLogic?.find(l => l.optionId === optionId);
        return logic?.isExclusive === true;
      };
      
      // Get all exclusive option IDs for this question
      const exclusiveOptionIds = (question.optionLogic || [])
        .filter(l => l.isExclusive === true)
        .map(l => l.optionId);
      
      // Check if any currently selected option is exclusive
      const hasExclusiveSelected = selectedMultiple.some(id => 
        exclusiveOptionIds.includes(id)
      );
      
      // Handle option selection with exclusive logic
      const handleOptionChange = (optionId: string, checked: boolean) => {
        if (checked) {
          if (isOptionExclusive(optionId)) {
            // Selecting an exclusive option: clear all others (including "Other")
            onChange([optionId]);
          } else if (hasExclusiveSelected) {
            // Selecting a non-exclusive option when exclusive is selected:
            // Clear exclusive options and add the new selection
            const withoutExclusive = selectedMultiple.filter(id => 
              !exclusiveOptionIds.includes(id)
            );
            onChange([...withoutExclusive, optionId]);
          } else {
            // Normal case: just add the option
            onChange([...selectedMultiple, optionId]);
          }
        } else {
          // Unchecking: just remove the option
          onChange(selectedMultiple.filter((o: string) => o !== optionId));
        }
      };
      
      // Handle "Other" option with exclusive logic
      const handleOtherChange = (checked: boolean) => {
        if (checked) {
          if (hasExclusiveSelected) {
            // Selecting "Other" when exclusive is selected: clear exclusive options
            const withoutExclusive = selectedMultiple.filter(id => 
              !exclusiveOptionIds.includes(id) && !id.startsWith("__other__:")
            );
            onChange([...withoutExclusive, "__other__:"]);
          } else {
            onChange([...selectedMultiple.filter(o => !o.startsWith("__other__:")), "__other__:"]);
          }
        } else {
          onChange(selectedMultiple.filter(o => !o.startsWith("__other__:")));
        }
      };
      
      return (
        <div className="space-y-2">
          {(question.options || []).map((option) => (
            <label 
              key={option.id} 
              className="flex items-center space-x-3 p-3 border border-border rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-700 cursor-pointer transition-all"
            >
              <Checkbox 
                checked={selectedMultiple.includes(option.id)}
                onCheckedChange={(checked) => handleOptionChange(option.id, !!checked)}
                className="text-purple-600 dark:text-purple-400"
              />
              <span className="text-foreground text-sm sm:text-base">{option.text}</span>
            </label>
          ))}
          {question.allowOther && (
            <div className="space-y-2">
              <label 
                className={`flex items-center space-x-3 p-3 border rounded-lg cursor-pointer transition-all ${
                  isOtherSelected 
                    ? 'border-purple-300 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20' 
                    : 'border-border hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:border-purple-200 dark:hover:border-purple-700'
                }`}
              >
                <Checkbox 
                  checked={isOtherSelected}
                  onCheckedChange={(checked) => handleOtherChange(!!checked)}
                  className="text-purple-600 dark:text-purple-400"
                />
                <span className="text-foreground text-sm sm:text-base">{question.otherLabel || "Other (please specify)"}</span>
              </label>
              {isOtherSelected && (
                <Input
                  value={otherText}
                  onChange={(e) => {
                    const withoutOther = selectedMultiple.filter(o => !o.startsWith("__other__:"));
                    onChange([...withoutOther, `__other__:${e.target.value}`]);
                  }}
                  placeholder="Please specify..."
                  className="ml-7 w-[calc(100%-1.75rem)] focus:ring-purple-500 focus:border-purple-500"
                  autoFocus
                />
              )}
            </div>
          )}
        </div>
      );
    }

    case "open_text":
      return (
        <Textarea 
          value={(value as string) || ""} 
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your answer here..."
          rows={4}
          className="w-full resize-none focus:ring-purple-500 focus:border-purple-500"
        />
      );

    case "dropdown":
      return (
        <Select value={(value as string) || ""} onValueChange={onChange}>
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Select an option..." />
          </SelectTrigger>
          <SelectContent>
            {(question.options || []).map((option) => (
              <SelectItem key={option.id} value={option.id}>{option.text}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );

    case "likert_scale": {
      const scale = question.scale || { min: 1, max: 5 };
      const scaleValues = Array.from(
        { length: scale.max - scale.min + 1 }, 
        (_, i) => scale.min + i
      );
      return (
        <div className="space-y-4">
          <RadioGroup 
            value={String(value || "")} 
            onValueChange={(v) => onChange(Number(v))}
            className="flex flex-wrap justify-between gap-2"
          >
            {scaleValues.map((val) => (
              <label 
                key={val} 
                className="flex flex-col items-center space-y-2 cursor-pointer group"
              >
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 flex items-center justify-center transition-all
                  ${value === val 
                    ? 'bg-purple-600 border-purple-600 text-white' 
                    : 'border-border text-muted-foreground group-hover:border-purple-400 dark:group-hover:border-purple-500'
                  }`}
                >
                  <RadioGroupItem value={String(val)} className="sr-only" />
                  <span className="font-semibold text-sm">{val}</span>
                </div>
                {scale.labels?.[val - scale.min] && (
                  <span className="text-xs text-muted-foreground text-center max-w-16">
                    {scale.labels[val - scale.min]}
                  </span>
                )}
              </label>
            ))}
          </RadioGroup>
          {scale.labels && scale.labels.length >= 2 && (
            <div className="flex justify-between text-xs text-muted-foreground px-2">
              <span>{scale.labels[0]}</span>
              <span>{scale.labels[scale.labels.length - 1]}</span>
            </div>
          )}
        </div>
      );
    }

    case "rating": {
      const ratingValue = (value as number) || 0;
      return (
        <div className="flex gap-1 sm:gap-2">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => onChange(star)}
              className={`p-1 sm:p-2 transition-all transform hover:scale-110 ${
                ratingValue >= star 
                  ? "text-yellow-400" 
                  : "text-muted-foreground/50 hover:text-yellow-200"
              }`}
            >
              <Star 
                className="w-8 h-8 sm:w-10 sm:h-10" 
                fill={ratingValue >= star ? "currentColor" : "none"}
              />
            </button>
          ))}
        </div>
      );
    }

    case "slider_range": {
      const sliderConfig = question.sliderRange || { min: 0, max: 100, step: 1 };
      const sliderValue = (value as number) ?? sliderConfig.min;
      const hasLabels = sliderConfig.labels && sliderConfig.labels.length > 0;
      
      return (
        <div className="space-y-4 px-2">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">Value:</span>
            <span className="text-lg font-bold text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/30 px-3 py-1 rounded-full">
              {sliderValue}
            </span>
          </div>
          <Slider
            value={[sliderValue]}
            onValueChange={([v]) => onChange(v)}
            min={sliderConfig.min}
            max={sliderConfig.max}
            step={sliderConfig.step || 1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {hasLabels ? (
              (sliderConfig.labels ?? []).map((label, idx) => (
                <span key={idx}>{label}</span>
              ))
            ) : (
              <>
                <span>{sliderConfig.min}</span>
                <span>{Math.round((sliderConfig.min + sliderConfig.max) / 2)}</span>
                <span>{sliderConfig.max}</span>
              </>
            )}
          </div>
        </div>
      );
    }

    case "date_time": {
      const config = question.dateTimeConfig ?? DEFAULT_DATE_TIME_CONFIG;
      const mode = config.mode;
      const inputType = mode === "date" ? "date" : mode === "time" ? "time" : "datetime-local";
      // Only apply step for time (normalizer handles datetime-local edge cases)
      const step = mode === "time" ? (config.minuteStep ?? 1) * 60 : undefined;
      
      // Normalizer: trim seconds if browser outputs them
      const normalizeValue = (val: string): string => {
        if (!val) return val;
        // Only slice if value matches expected "with seconds" length
        if (mode === "time" && val.length >= 8) {
          // HH:MM:SS → HH:MM
          return val.slice(0, 5);
        }
        if (mode === "datetime" && val.length >= 19) {
          // YYYY-MM-DDTHH:MM:SS → YYYY-MM-DDTHH:MM
          return val.slice(0, 16);
        }
        return val;
      };
      
      return (
        <Input 
          type={inputType}
          step={step}
          value={(value as string) || ""} 
          onChange={(e) => onChange(normalizeValue(e.target.value))}
          className="w-full focus:ring-purple-500 focus:border-purple-500"
        />
      );
    }

    case "ranking": {
      // Key combines question identity with options identity
      // Using option IDs for stable keys
      const rankingKey = `${question.id}-${(question.options || []).map(o => o.id).join('|')}`;
      return (
        <RankingInput 
          key={rankingKey}
          question={question} 
          value={value as string[] | undefined} 
          onChange={onChange} 
        />
      );
    }

    case "file_upload":
      return (
        <FileUploadInput
          question={question}
          value={value}
          onChange={onChange}
          shareToken={shareToken}
        />
      );

    // Matrix question types
    case "radio_grid":
    case "checkbox_grid": {
      const currentMatrixValue = (value as Record<string, number | number[]>) || {};
      const columns = question.columns || [];
      const rows = question.rows || [];

      // Mobile: Card-based layout with vertical options
      if (isMobile) {
        return (
          <div className="space-y-4">
            {rows.map((row, rowIndex) => (
              <div key={row.id} className="bg-muted rounded-lg p-4 border border-border">
                <p className="font-medium text-foreground mb-3 text-sm">{row.label}</p>
                {question.type === "radio_grid" ? (
                  <RadioGroup
                    value={currentMatrixValue[rowIndex]?.toString() ?? ""}
                    onValueChange={(val) => onChange({ ...currentMatrixValue, [rowIndex]: parseInt(val) })}
                    className="space-y-2"
                  >
                    {columns.map((col, colIndex) => {
                      const isSelected = currentMatrixValue[rowIndex] === colIndex;
                      return (
                        <label 
                          key={col.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                            ${isSelected 
                              ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700' 
                              : 'bg-card border-border hover:border-purple-200 dark:hover:border-purple-700'}`}
                        >
                          <RadioGroupItem 
                            value={colIndex.toString()}
                            className="text-purple-600 dark:text-purple-400"
                          />
                          <span className="text-sm text-foreground">{col.label}</span>
                        </label>
                      );
                    })}
                  </RadioGroup>
                ) : (
                  <div className="space-y-2">
                    {columns.map((col, colIndex) => {
                      const rowValues = (currentMatrixValue[rowIndex] as number[]) || [];
                      const isChecked = rowValues.includes(colIndex);
                      return (
                        <label 
                          key={col.id}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors
                            ${isChecked 
                              ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-300 dark:border-purple-700' 
                              : 'bg-card border-border hover:border-purple-200 dark:hover:border-purple-700'}`}
                        >
                          <Checkbox 
                            checked={isChecked}
                            onCheckedChange={(checked) => {
                              const newRowValues = checked 
                                ? [...rowValues, colIndex]
                                : rowValues.filter((v: number) => v !== colIndex);
                              onChange({ ...currentMatrixValue, [rowIndex]: newRowValues });
                            }}
                            className="text-purple-600 dark:text-purple-400"
                          />
                          <span className="text-sm text-foreground">{col.label}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      }

      // Desktop: Table layout
      return (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-border bg-muted"></th>
                {columns.map((col) => (
                  <th key={col.id} className="text-center p-2 border-b border-border bg-muted font-medium text-foreground min-w-20">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.id} className="border-b border-border">
                  <td className="p-2 font-medium text-foreground bg-muted">{row.label}</td>
                  {question.type === "radio_grid" ? (
                    // Use native radio inputs for table grids to avoid invalid HTML nesting
                    // (RadioGroup renders a <div> which cannot be a child of <tr>)
                    columns.map((col, colIndex) => (
                      <td key={col.id} className="text-center p-2">
                        <input
                          type="radio"
                          name={`${question.id}-row-${rowIndex}`}
                          value={colIndex.toString()}
                          checked={currentMatrixValue[rowIndex]?.toString() === colIndex.toString()}
                          onChange={() => onChange({ ...currentMatrixValue, [rowIndex]: colIndex })}
                          className="h-4 w-4 text-purple-600 border-border focus:ring-purple-500 cursor-pointer"
                        />
                      </td>
                    ))
                  ) : (
                    columns.map((col, colIndex) => {
                      const rowValues = (currentMatrixValue[rowIndex] as number[]) || [];
                      return (
                        <td key={col.id} className="text-center p-2">
                          <Checkbox 
                            checked={rowValues.includes(colIndex)}
                            onCheckedChange={(checked) => {
                              const newRowValues = checked 
                                ? [...rowValues, colIndex]
                                : rowValues.filter((v: number) => v !== colIndex);
                              onChange({ ...currentMatrixValue, [rowIndex]: newRowValues });
                            }}
                            className="text-purple-600 dark:text-purple-400"
                          />
                        </td>
                      );
                    })
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "dropdown_matrix": {
      const dropdownOpts = question.dropdownMatrixOptions?.shared || ["Option 1", "Option 2", "Option 3"];
      const dropdownMatrixValue = (value as Record<string, string>) || {};
      const columns = question.columns || [];
      const rows = question.rows || [];

      // Mobile: Card-based layout
      if (isMobile) {
        return (
          <div className="space-y-4">
            {rows.map((row, rowIndex) => (
              <div key={row.id} className="bg-muted rounded-lg p-4 border border-border">
                <p className="font-medium text-foreground mb-3 text-sm">{row.label}</p>
                <div className="space-y-3">
                  {columns.map((col, colIndex) => {
                    const cellValue = dropdownMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <div key={col.id} className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground font-medium">{col.label}</label>
                        <Select 
                          value={cellValue} 
                          onValueChange={(v) => onChange({ 
                            ...dropdownMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: v 
                          })}
                        >
                          <SelectTrigger className="w-full h-10 text-sm">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {dropdownOpts.map((opt, i) => (
                              <SelectItem key={i} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Desktop: Table layout
      return (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-border bg-muted"></th>
                {columns.map((col) => (
                  <th key={col.id} className="text-center p-2 border-b border-border bg-muted font-medium text-foreground min-w-28">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.id} className="border-b border-border">
                  <td className="p-2 font-medium text-foreground bg-muted">{row.label}</td>
                  {columns.map((col, colIndex) => {
                    const cellValue = dropdownMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <td key={col.id} className="p-2">
                        <Select 
                          value={cellValue} 
                          onValueChange={(v) => onChange({ 
                            ...dropdownMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: v 
                          })}
                        >
                          <SelectTrigger className="w-full h-8 text-xs">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {dropdownOpts.map((opt, i) => (
                              <SelectItem key={i} value={opt}>{opt}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "text_matrix":
    case "numeric_matrix": {
      const textNumericMatrixValue = (value as Record<string, string>) || {};
      const columns = question.columns || [];
      const rows = question.rows || [];

      // Mobile: Card-based layout
      if (isMobile) {
        return (
          <div className="space-y-4">
            {rows.map((row, rowIndex) => (
              <div key={row.id} className="bg-muted rounded-lg p-4 border border-border">
                <p className="font-medium text-foreground mb-3 text-sm">{row.label}</p>
                <div className="space-y-3">
                  {columns.map((col, colIndex) => {
                    const cellValue = textNumericMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <div key={col.id} className="flex flex-col gap-1">
                        <label className="text-xs text-muted-foreground font-medium">{col.label}</label>
                        <Input
                          type={question.type === "numeric_matrix" ? "number" : "text"}
                          value={cellValue}
                          onChange={(e) => onChange({ 
                            ...textNumericMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: e.target.value 
                          })}
                          placeholder={question.type === "numeric_matrix" ? "0" : "Enter text..."}
                          className="h-10 text-sm"
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Desktop: Table layout
      return (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-border bg-muted"></th>
                {columns.map((col) => (
                  <th key={col.id} className="text-center p-2 border-b border-border bg-muted font-medium text-foreground min-w-24">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.id} className="border-b border-border">
                  <td className="p-2 font-medium text-foreground bg-muted">{row.label}</td>
                  {columns.map((col, colIndex) => {
                    const cellValue = textNumericMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <td key={col.id} className="p-2">
                        <Input
                          type={question.type === "numeric_matrix" ? "number" : "text"}
                          value={cellValue}
                          onChange={(e) => onChange({ 
                            ...textNumericMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: e.target.value 
                          })}
                          placeholder={question.type === "numeric_matrix" ? "0" : "..."}
                          className="h-8 text-sm"
                        />
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "ranking_matrix": {
      const rankingMatrixValue = (value as Record<string, string>) || {};
      const columns = question.columns || [];
      const rows = question.rows || [];
      const numRows = rows.length || 1;

      // Mobile: Card-based layout
      if (isMobile) {
        return (
          <div className="space-y-4">
            {rows.map((row, rowIndex) => (
              <div key={row.id} className="bg-muted rounded-lg p-4 border border-border">
                <p className="font-medium text-foreground mb-3 text-sm">{row.label}</p>
                <div className="space-y-3">
                  {columns.map((col, colIndex) => {
                    const cellValue = rankingMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <div key={col.id} className="flex items-center justify-between gap-3">
                        <label className="text-sm text-foreground flex-1">{col.label}</label>
                        <Select 
                          value={cellValue} 
                          onValueChange={(v) => onChange({ 
                            ...rankingMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: v 
                          })}
                        >
                          <SelectTrigger className="w-20 h-10 text-sm">
                            <SelectValue placeholder="Rank" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: numRows }, (_, i) => (
                              <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        );
      }

      // Desktop: Table layout
      return (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full min-w-max border-collapse text-sm">
            <thead>
              <tr>
                <th className="text-left p-2 border-b border-border bg-muted"></th>
                {columns.map((col) => (
                  <th key={col.id} className="text-center p-2 border-b border-border bg-muted font-medium text-foreground min-w-20">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={row.id} className="border-b border-border">
                  <td className="p-2 font-medium text-foreground bg-muted">{row.label}</td>
                  {columns.map((col, colIndex) => {
                    const cellValue = rankingMatrixValue[`${rowIndex}-${colIndex}`] || "";
                    return (
                      <td key={col.id} className="p-2">
                        <Select 
                          value={cellValue} 
                          onValueChange={(v) => onChange({ 
                            ...rankingMatrixValue, 
                            [`${rowIndex}-${colIndex}`]: v 
                          })}
                        >
                          <SelectTrigger className="w-16 h-8 text-xs mx-auto">
                            <SelectValue placeholder="#" />
                          </SelectTrigger>
                          <SelectContent>
                            {Array.from({ length: numRows }, (_, i) => (
                              <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    case "video_response":
      return (
        <VideoResponseRecorder
          questionId={question.id}
          shareToken={shareToken}
          maxDuration={question.videoConfig?.maxDuration || 60}
          allowRetake={question.videoConfig?.allowRetake ?? true}
          value={value as VideoUploadValue | null}
          onChange={(val) => onChange(val as AnswerValue)}
        />
      );

    case "image_choice": {
      const imageOptions = question.imageOptions;
      const images = imageOptions?.images || [];
      const selectionMode = imageOptions?.selectionMode || "single";
      const allowNoneOfAbove = imageOptions?.allowNoneOfAbove || false;
      const noneOfAboveLabel = imageOptions?.noneOfAboveLabel || "None of the above";
      
      // For single select, value is a string (index or "__none__")
      // For multiple select, value is an array of indices or includes "__none__"
      const selectedSingle = selectionMode === "single" ? (value as string) || "" : "";
      const selectedMultiple = selectionMode === "multiple" ? (value as string[]) || [] : [];
      
      const isNoneSelected = selectionMode === "single" 
        ? selectedSingle === "__none__"
        : selectedMultiple.includes("__none__");

      const handleImageSelect = (index: number) => {
        const indexStr = String(index);
        if (selectionMode === "single") {
          onChange(indexStr);
        } else {
          // Multiple selection
          const current = selectedMultiple.filter(s => s !== "__none__");
          if (current.includes(indexStr)) {
            onChange(current.filter(s => s !== indexStr));
          } else {
            onChange([...current, indexStr]);
          }
        }
      };

      const handleNoneOfAboveSelect = () => {
        if (selectionMode === "single") {
          onChange(selectedSingle === "__none__" ? "" : "__none__");
        } else {
          if (isNoneSelected) {
            onChange(selectedMultiple.filter(s => s !== "__none__"));
          } else {
            // Selecting "none" clears other selections
            onChange(["__none__"]);
          }
        }
      };

      const isImageSelected = (index: number): boolean => {
        const indexStr = String(index);
        if (selectionMode === "single") {
          return selectedSingle === indexStr;
        } else {
          return selectedMultiple.includes(indexStr);
        }
      };

      return (
        <div className="space-y-4">
          {/* Image Grid */}
          <div className={`grid gap-4 ${isMobile ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {images.map((img, idx) => {
              const isSelected = isImageSelected(idx);
              const imageUrl = getImageDisplayUrl(img.url);
              const isLoading = img.url && img.url.startsWith("survey-uploads/") && !imageUrl;
              
              return (
                <button
                  key={idx}
                  type="button"
                  onClick={() => handleImageSelect(idx)}
                  disabled={isNoneSelected}
                  className={`
                    relative rounded-xl overflow-hidden border-2 transition-all duration-200
                    ${isSelected 
                      ? 'border-purple-500 ring-2 ring-purple-200 dark:ring-purple-800 shadow-lg' 
                      : 'border-border hover:border-purple-300 dark:hover:border-purple-600'
                    }
                    ${isNoneSelected ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                    focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
                  `}
                  aria-label={img.altText || img.caption || `Image option ${idx + 1}`}
                >
                  {/* Image Container */}
                  <div className="aspect-square bg-muted relative">
                    {isLoading ? (
                      <div className="w-full h-full flex items-center justify-center">
                        <Loader2 className="w-8 h-8 text-primary animate-spin" />
                      </div>
                    ) : imageUrl ? (
                      <img 
                        src={imageUrl}
                        alt={img.altText || img.caption || `Option ${idx + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-12 h-12 text-muted-foreground/50" />
                      </div>
                    )}
                    
                    {/* Selection Indicator */}
                    <div className={`
                      absolute top-2 right-2 w-6 h-6 rounded-full border-2 flex items-center justify-center
                      transition-all duration-200
                      ${isSelected 
                        ? 'bg-purple-500 border-purple-500' 
                        : 'bg-white/80 dark:bg-card/80 border-border'
                      }
                    `}>
                      {isSelected && (
                        <CheckCircle2 className="w-5 h-5 text-white" />
                      )}
                      {!isSelected && selectionMode === "multiple" && (
                        <div className="w-3 h-3 rounded-sm border border-muted-foreground" />
                      )}
                    </div>
                  </div>
                  
                  {/* Caption */}
                  {img.caption && (
                    <div className={`
                      p-2 text-sm text-center font-medium transition-colors
                      ${isSelected ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300' : 'bg-muted text-foreground'}
                    `}>
                      {img.caption}
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* None of the above option */}
          {allowNoneOfAbove && (
            <button
              type="button"
              onClick={handleNoneOfAboveSelect}
              className={`
                w-full p-4 rounded-lg border-2 transition-all duration-200
                flex items-center justify-center gap-3
                ${isNoneSelected 
                  ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300' 
                  : 'border-border hover:border-purple-300 dark:hover:border-purple-600 text-muted-foreground'
                }
                focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2
              `}
            >
              <div className={`
                w-5 h-5 rounded-full border-2 flex items-center justify-center
                ${isNoneSelected 
                  ? 'bg-purple-500 border-purple-500' 
                  : 'border-muted-foreground'
                }
              `}>
                {isNoneSelected && (
                  <CheckCircle2 className="w-4 h-4 text-white" />
                )}
              </div>
              <span className="font-medium">{noneOfAboveLabel}</span>
            </button>
          )}

          {/* Selection hint */}
          <p className="text-xs text-muted-foreground text-center">
            {selectionMode === "single" 
              ? "Click to select one image" 
              : "Click to select multiple images"
            }
          </p>
        </div>
      );
    }

    case "heat_map": {
      // Render the heat map question using a separate component to properly use hooks
      return (
        <HeatMapQuestionInput 
          question={question}
          value={value}
          onChange={onChange}
          getImageDisplayUrl={getImageDisplayUrl}
        />
      );
    }

    case "pick_group_rank": {
      const config = question.pickGroupRankConfig;
      if (!config) {
        return <p className="text-sm text-muted-foreground">Configuration error: No pick/group/rank config found.</p>;
      }
      
      const pgrValue = isPickGroupRankAnswer(value) ? value : null;
      
      return (
        <PickGroupRankInput
          config={config}
          value={pgrValue}
          onChange={(newAnswer) => onChange(newAnswer as AnswerValue)}
          isMobile={isMobile}
        />
      );
    }

    default:
      return (
        <Input 
          value={(value as string) || ""} 
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter your response..."
          className="w-full focus:ring-purple-500 focus:border-purple-500"
        />
      );
  }
}

